/**
 * Generic keyed-record store toolkit — extracted from the proven internals
 * of `crmObjectStore.ts`. A single Map<id, StoredRecord<T>> with snapshot
 * identity for `useSyncExternalStore`, per-record subscriptions, a
 * seq-guarded structural-merge upsert, batch mutation support, and
 * size-capped LRU eviction. Public types live in `recordStoreTypes.ts`;
 * reads, upsert, and eviction each have their own composed engine module
 * (`recordStoreReads.ts`, `recordStoreUpsert.ts`, `recordStoreEviction.ts`)
 * to keep this file — the batch-aware commit/remove/subscribe core — under
 * the project's line-count guidance.
 *
 * This is deliberately generic: no React, no CRM-specific merge rules
 * (aliases, email index, pending creations/associations, pkIndex). Callers
 * that need to protect additional records beyond active subscribers (e.g.
 * pending optimistic creations) inject `isProtected` via
 * `CreateRecordStoreOptions`.
 */

import { createEvictionEngine } from './recordStoreEviction';
import { createReadEngine } from './recordStoreReads';
import type {
  CreateRecordStoreOptions,
  RecordStore,
  SeqResult,
  StoredRecord,
} from './recordStoreTypes';
import { createUpsertEngine } from './recordStoreUpsert';

export type {
  CreateRecordStoreOptions,
  EvictedRecord,
  RecordMeta,
  RecordStore,
  SeqResult,
  StoredRecord,
} from './recordStoreTypes';

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createRecordStore<T>(
  options: CreateRecordStoreOptions<T> = {}
): RecordStore<T> {
  let records = new Map<string, StoredRecord<T>>();
  /** Snapshot reference — replaced on every mutation for identity checks. */
  let snapshot: ReadonlyMap<string, StoredRecord<T>> = records;
  let listeners = new Set<() => void>();
  let recordListeners = new Map<string, Set<() => void>>();
  /** Stable per-record data cache — mirrors `records` for a lighter read. */
  let recordSnapshots = new Map<string, T | undefined>();

  /** Nesting depth — only the outermost endBatch commits. */
  let batchDepth = 0;
  /** Working copy of `records` during a batch — mutated in place. */
  let batchRecords: Map<string, StoredRecord<T>> | null = null;
  /** Working copy of `recordSnapshots` during a batch — mutated in place. */
  let batchSnapshots: Map<string, T | undefined> | null = null;
  /** Ids that changed during the current batch (for per-record notifications). */
  let batchChangedIds: string[] = [];

  function emitChange(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function emitRecordChange(id: string): void {
    const set = recordListeners.get(id);
    if (set) {
      for (const listener of set) {
        listener();
      }
    }
    for (const key of options.extraChangeKeys?.(id) ?? []) {
      const extraSet = recordListeners.get(key);
      if (extraSet) {
        for (const listener of extraSet) {
          listener();
        }
      }
    }
  }

  function checkSeq(storedSeq: number, eventSeq: number): SeqResult {
    if (eventSeq <= storedSeq) {
      return 'skipped';
    }
    if (eventSeq > storedSeq + 1) {
      return 'gap';
    }
    return 'applied';
  }

  // -- Size-capped LRU eviction (delegated to recordStoreEviction.ts) ---------

  const eviction = createEvictionEngine<T>({
    getRecords: () => records,
    setRecords: m => {
      records = m;
    },
    getRecordSnapshots: () => recordSnapshots,
    setRecordSnapshots: m => {
      recordSnapshots = m;
    },
    setSnapshot: m => {
      snapshot = m;
    },
    getBatchDepth: () => batchDepth,
    getRecordListeners: () => recordListeners,
    emitRecordChange,
    emitChange,
    isProtected: options.isProtected,
    maxEntries: options.maxEntries,
  });

  // -- Low-level batch-aware mutation ----------------------------------------

  function commit(id: string, record: StoredRecord<T>): void {
    if (batchDepth > 0) {
      if (!batchRecords || !batchSnapshots) {
        throw new Error('Batch state inconsistency');
      }
      batchRecords.set(id, record);
      batchSnapshots.set(id, record.data);
      batchChangedIds.push(id);
      return;
    }

    records = new Map(records);
    records.set(id, record);
    recordSnapshots = new Map(recordSnapshots);
    recordSnapshots.set(id, record.data);
    snapshot = records;
    emitRecordChange(id);
    emitChange();
    eviction.touch(id);
    eviction.afterBatchCommit();
  }

  function remove(id: string): void {
    if (batchDepth > 0) {
      if (!batchRecords || !batchSnapshots) {
        throw new Error('Batch state inconsistency');
      }
      if (!batchRecords.has(id)) {
        return;
      }
      batchRecords.delete(id);
      batchSnapshots.delete(id);
      eviction.forget(id);
      batchChangedIds.push(id);
      return;
    }

    if (!records.has(id)) {
      return;
    }
    records = new Map(records);
    records.delete(id);
    recordSnapshots = new Map(recordSnapshots);
    recordSnapshots.delete(id);
    snapshot = records;
    eviction.forget(id);
    emitRecordChange(id);
    emitChange();
  }

  function isBatching(): boolean {
    return batchDepth > 0;
  }

  // -- Reads (delegated to recordStoreReads.ts) --------------------------------

  const reads = createReadEngine<T>({
    getRecords: () => records,
    getSnapshot: () => snapshot,
    getBatchDepth: () => batchDepth,
    getBatchRecords: () => batchRecords,
  });

  // -- Global subscription (useSyncExternalStore) ------------------------------

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function notify(): void {
    emitChange();
  }

  function notifyRecord(id: string): void {
    if (batchDepth > 0) {
      batchChangedIds.push(id);
      return;
    }
    emitRecordChange(id);
    emitChange();
  }

  // -- Per-record subscription (useSyncExternalStore) ---------------------------

  function subscribeToRecord(id: string): {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => StoredRecord<T> | undefined;
  } {
    return {
      subscribe: (listener: () => void) => {
        let set = recordListeners.get(id);
        if (!set) {
          set = new Set();
          recordListeners.set(id, set);
        }
        set.add(listener);
        // Only LRU-protect ids with committed data — touching an id that
        // never commits (subscribed-to but never fetched) would leave a
        // permanent phantom entry in the eviction engine's LRU list, since
        // `enforceSizeCap` skips (never removes) entries absent from
        // `records`. `commit()`'s own `touch()` call covers the record once
        // data actually arrives.
        if (records.has(id)) {
          eviction.touch(id);
        }
        const capturedSet = set;
        return () => {
          capturedSet.delete(listener);
          if (capturedSet.size === 0) {
            recordListeners.delete(id);
          }
        };
      },
      getSnapshot: () => reads.getRecord(id),
    };
  }

  // -- Generic seq-guarded upsert (delegated to recordStoreUpsert.ts) ---------

  const upsertEngine = createUpsertEngine<T>({
    getRecord: reads.getRecord,
    checkSeq,
    commit,
  });

  // -- Batch mutation -----------------------------------------------------------

  function beginBatch(): void {
    if (batchDepth === 0) {
      batchRecords = new Map(records);
      batchSnapshots = new Map(recordSnapshots);
      batchChangedIds = [];
    }
    batchDepth++;
  }

  function endBatch(): void {
    if (batchDepth <= 0) {
      return;
    }
    batchDepth--;
    if (batchDepth > 0) {
      return;
    }

    if (batchChangedIds.length > 0) {
      if (!batchRecords || !batchSnapshots) {
        throw new Error('Batch state inconsistency');
      }
      records = batchRecords;
      recordSnapshots = batchSnapshots;
      snapshot = records;

      for (const id of batchChangedIds) {
        eviction.touch(id);
      }
      eviction.afterBatchCommit();

      const uniqueIds = [...new Set(batchChangedIds)];
      for (const id of uniqueIds) {
        emitRecordChange(id);
      }
      emitChange();
    }

    batchRecords = null;
    batchSnapshots = null;
    batchChangedIds = [];
  }

  // -- Lifecycle ------------------------------------------------------------------

  function reset(): void {
    records = new Map();
    recordSnapshots = new Map();
    snapshot = records;
    listeners = new Set();
    recordListeners = new Map();
    batchDepth = 0;
    batchRecords = null;
    batchSnapshots = null;
    batchChangedIds = [];
    eviction.reset();
  }

  return {
    getRecord: reads.getRecord,
    getData: reads.getData,
    getSeq: reads.getSeq,
    getSnapshot: reads.getSnapshot,
    hasComplete: reads.hasComplete,
    getCompleteIds: reads.getCompleteIds,
    isStale: reads.isStale,
    subscribe,
    notify,
    notifyRecord,
    subscribeToRecord,
    checkSeq,
    commit,
    remove,
    isBatching,
    upsert: upsertEngine.upsert,
    beginBatch,
    endBatch,
    reset,
    touch: eviction.touch,
    forget: eviction.forget,
    enforceSizeCap: eviction.enforceSizeCap,
    setMaxEntries: eviction.setMaxEntries,
    subscribeToEviction: eviction.subscribeToEviction,
  };
}
